export const ChatOutboxStatus = {
  QUEUED: "queued",
  UPLOADING: "uploading",
  ERROR: "error",
};

export const ChatOutboxErrorStage = {
  MEDIA: "media",
  MESSAGE: "message",
};

const DB_NAME = "efort-chat-outbox-v1";
const STORE_NAME = "messages";
const DB_VERSION = 1;
const BASE_RETRY_DELAY_MS = 3000;
const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;

const getIndexedDB = () => {
  if (typeof window === "undefined") return null;
  return window.indexedDB || null;
};

const normalizeId = (value) => String(value ?? "").trim().toLowerCase();

export const getChatOutboxRetryDelayMs = (attempts = 0) => {
  const exponent = Math.max(0, Number(attempts) || 0);
  return Math.min(BASE_RETRY_DELAY_MS * 2 ** Math.min(exponent, 8), MAX_RETRY_DELAY_MS);
};

export const isChatOutboxRecordReady = (record, nowMs = Date.now()) => {
  if (!record) return false;
  const nextAttemptAtMs = Number(record.nextAttemptAtMs) || 0;
  return nextAttemptAtMs <= nowMs;
};

export const sortChatOutboxRecords = (records = []) =>
  [...records].sort((a, b) => {
    const createdDelta = (Number(a.createdAtMs) || 0) - (Number(b.createdAtMs) || 0);
    if (createdDelta !== 0) return createdDelta;

    const groupDelta = String(a.groupId || "").localeCompare(String(b.groupId || ""));
    if (groupDelta !== 0) return groupDelta;

    return (Number(a.sequence) || 0) - (Number(b.sequence) || 0);
  });

const toTimestampMs = (timestamp) => {
  if (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())) {
    return timestamp.getTime();
  }

  if (typeof timestamp === "number" && Number.isFinite(timestamp)) {
    return timestamp;
  }

  const parsed = new Date(timestamp);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getTime();
  }

  return Date.now();
};

export const buildChatOutboxRecord = (message, options = {}) => {
  const nowMs = Number(options.nowMs) || Date.now();
  const coachId = String(options.coachId ?? message?.client?.coach?.id ?? "").trim();
  const clientId = String(options.clientId ?? message?.client?.id ?? "").trim();
  const id = String(message?.id ?? "").trim();

  if (!id || !coachId || !clientId) {
    throw new Error("Cannot queue chat message without id, coachId, and clientId.");
  }

  return {
    id,
    coachId,
    clientId,
    type: message?.type ?? "text",
    text: message?.text ?? "",
    timestampMs: toTimestampMs(message?.timestamp),
    isFromCoach: message?.isFromCoach !== false,
    replyTo: message?.replyTo ?? null,
    attachment: message?.attachment ?? null,
    totalTime: message?.totalTime ?? 0,
    serieId: message?.serieId ?? null,
    workoutId: message?.workoutId ?? null,
    blockId: message?.blockId ?? null,
    originalAttachmentURL: message?.originalAttachmentURL ?? null,
    status: options.status ?? ChatOutboxStatus.QUEUED,
    errorStage: options.errorStage ?? null,
    attempts: Number(options.attempts) || 0,
    lastError: options.lastError ?? null,
    createdAtMs: Number(options.createdAtMs) || nowMs,
    nextAttemptAtMs: Number(options.nextAttemptAtMs) || 0,
    groupId: options.groupId ?? id,
    sequence: Number(options.sequence) || 0,
  };
};

class ChatOutboxManager {
  static instance = null;

  dbPromise = null;
  memoryRecords = new Map();

  static getInstance() {
    if (!ChatOutboxManager.instance) {
      ChatOutboxManager.instance = new ChatOutboxManager();
    }
    return ChatOutboxManager.instance;
  }

  get supportsIndexedDB() {
    return Boolean(getIndexedDB());
  }

  openDatabase() {
    const indexedDB = getIndexedDB();
    if (!indexedDB) {
      return Promise.resolve(null);
    }

    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        const store = db.objectStoreNames.contains(STORE_NAME)
          ? request.transaction.objectStore(STORE_NAME)
          : db.createObjectStore(STORE_NAME, { keyPath: "id" });

        if (!store.indexNames.contains("coachId")) {
          store.createIndex("coachId", "coachId", { unique: false });
        }
        if (!store.indexNames.contains("clientId")) {
          store.createIndex("clientId", "clientId", { unique: false });
        }
        if (!store.indexNames.contains("status")) {
          store.createIndex("status", "status", { unique: false });
        }
        if (!store.indexNames.contains("nextAttemptAtMs")) {
          store.createIndex("nextAttemptAtMs", "nextAttemptAtMs", {
            unique: false,
          });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        this.dbPromise = null;
        reject(request.error || new Error("Failed to open chat outbox database."));
      };
      request.onblocked = () => {
        this.dbPromise = null;
        reject(new Error("Chat outbox database upgrade was blocked."));
      };
    });

    return this.dbPromise;
  }

  async withStore(mode, callback) {
    const db = await this.openDatabase();
    if (!db) {
      return callback(null);
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      let callbackResult;

      transaction.oncomplete = () => resolve(callbackResult);
      transaction.onerror = () =>
        reject(transaction.error || new Error("Chat outbox transaction failed."));
      transaction.onabort = () =>
        reject(transaction.error || new Error("Chat outbox transaction aborted."));

      try {
        callbackResult = callback(store);
      } catch (error) {
        transaction.abort();
        reject(error);
      }
    });
  }

  requestToPromise(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async enqueueMessages(records) {
    const normalizedRecords = Array.isArray(records) ? records : [records];
    if (normalizedRecords.length === 0) return;

    if (!this.supportsIndexedDB) {
      normalizedRecords.forEach((record) => {
        this.memoryRecords.set(record.id, record);
      });
      return;
    }

    await this.withStore("readwrite", (store) => {
      normalizedRecords.forEach((record) => {
        store.put(record);
      });
    });
  }

  async getMessage(id) {
    if (!id) return null;

    if (!this.supportsIndexedDB) {
      return this.memoryRecords.get(id) ?? null;
    }

    return this.withStore("readonly", (store) => this.requestToPromise(store.get(id)));
  }

  async getMessagesForClient(coachId, clientId) {
    const normalizedCoachId = normalizeId(coachId);
    const normalizedClientId = normalizeId(clientId);

    if (!this.supportsIndexedDB) {
      return sortChatOutboxRecords(
        Array.from(this.memoryRecords.values()).filter(
          (record) =>
            normalizeId(record.coachId) === normalizedCoachId &&
            normalizeId(record.clientId) === normalizedClientId,
        ),
      );
    }

    const records = await this.withStore("readonly", (store) =>
      this.requestToPromise(store.getAll()),
    );

    return sortChatOutboxRecords(
      (records || []).filter(
        (record) =>
          normalizeId(record.coachId) === normalizedCoachId &&
          normalizeId(record.clientId) === normalizedClientId,
      ),
    );
  }

  async getReadyMessagesForClient(coachId, clientId, nowMs = Date.now()) {
    const records = await this.getMessagesForClient(coachId, clientId);
    return records.filter((record) => isChatOutboxRecordReady(record, nowMs));
  }

  async countForClient(coachId, clientId) {
    const records = await this.getMessagesForClient(coachId, clientId);
    return records.length;
  }

  async updateMessage(id, patch) {
    if (!id) return;

    if (!this.supportsIndexedDB) {
      const existing = this.memoryRecords.get(id);
      if (existing) {
        this.memoryRecords.set(id, { ...existing, ...patch });
      }
      return;
    }

    await this.withStore("readwrite", (store) => {
      const request = store.get(id);
      request.onsuccess = () => {
        if (!request.result) return;
        store.put({ ...request.result, ...patch });
      };
    });
  }

  async markAttempt(id, nowMs = Date.now()) {
    const record = await this.getMessage(id);
    if (!record) return null;

    const attempts = (Number(record.attempts) || 0) + 1;
    const updatedRecord = {
      ...record,
      status: ChatOutboxStatus.UPLOADING,
      attempts,
      lastAttemptAtMs: nowMs,
      lastError: null,
      errorStage: null,
    };

    await this.updateMessage(id, updatedRecord);
    return updatedRecord;
  }

  async markError(id, { error, stage, nowMs = Date.now() } = {}) {
    const record = await this.getMessage(id);
    if (!record) return;

    const attempts = Number(record.attempts) || 0;
    await this.updateMessage(id, {
      status: ChatOutboxStatus.ERROR,
      errorStage: stage ?? null,
      lastError: error?.message ?? String(error ?? "Unknown chat outbox error"),
      nextAttemptAtMs: nowMs + getChatOutboxRetryDelayMs(attempts),
    });
  }

  async removeMessage(id) {
    if (!id) return;

    if (!this.supportsIndexedDB) {
      this.memoryRecords.delete(id);
      return;
    }

    await this.withStore("readwrite", (store) => {
      store.delete(id);
    });
  }
}

export default ChatOutboxManager;
